import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import './App.css';
import Login from './Login';
import Dashboard from './Dashboard';
import RecuperarSenha from './RecuperarSenha';
import VerificarEmail from './VerificarEmail';
import AdminSecurityGate from './AdminSecurityGate';
import PaginaPrivacidadePublica from './PaginaPrivacidadePublica';
import PaginaSolicitacaoExclusaoConta from './PaginaSolicitacaoExclusaoConta';
import { IS_HOMOLOGACAO, auth, db } from './firebase';

const CONTEXTO_ACESSO_INICIAL = {
  isAdmin: false,
  isTechnical: false,
  requiresSecurityGate: false,
  perfisAtivos: [],
  telefone: '',
};

function TelaCarregandoPainel() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
      <div style={{ textAlign: 'center', color: '#374151' }}>
        <div style={{ marginBottom: '14px', fontWeight: 'bold' }}>Preparando painel...</div>
        <div style={{ width: '42px', height: '42px', borderRadius: '50%', border: '4px solid #e5e7eb', borderTopColor: '#e53935', margin: '0 auto', animation: 'spin 0.8s linear infinite' }} />
      </div>
    </div>
  );
}

function SeloHomologacao() {
  if (!IS_HOMOLOGACAO) {
    return null;
  }

  return (
    <div className="environment-badge" role="status" aria-label="Ambiente de homologação">
      <span>AMBIENTE DE</span>
      HOMOLOGAÇÃO
    </div>
  );
}

function TelaFalhaPainel({ mensagem, onRetry }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa', padding: '24px' }}>
      <div style={{ maxWidth: '460px', width: '100%', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '20px', padding: '28px', boxShadow: '0 18px 40px rgba(15, 23, 42, 0.08)' }}>
        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', marginBottom: '10px' }}>Validação de segurança pendente</div>
        <div style={{ color: '#4b5563', lineHeight: 1.6, marginBottom: '18px' }}>
          {mensagem || 'Não foi possível concluir a validação de segurança do painel agora.'}
        </div>
        <button
          type="button"
          onClick={onRetry}
          style={{ border: 'none', borderRadius: '12px', padding: '12px 18px', fontWeight: 'bold', backgroundColor: '#0f5ca8', color: '#fff', cursor: 'pointer' }}
        >
          Tentar novamente
        </button>
      </div>
    </div>
  );
}

function App() {
  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const [modoRecuperacao, setModoRecuperacao] = useState(false);
  const [modoVerificacao, setModoVerificacao] = useState(false);
  const [carregandoAcesso, setCarregandoAcesso] = useState(false);
  const [contextoAcesso, setContextoAcesso] = useState(CONTEXTO_ACESSO_INICIAL);
  const [erroAcesso, setErroAcesso] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('mode') === 'resetPassword') {
      setModoRecuperacao(true);
    } else if (urlParams.get('mode') === 'verifyEmail') {
      setModoVerificacao(true);
    }

    const vigia = onAuthStateChanged(auth, (user) => {
      setUsuarioLogado(user || null);
    });

    return () => vigia();
  }, []);

  useEffect(() => {
    let ativo = true;

    async function carregarContextoAcesso() {
      if (!usuarioLogado?.uid) {
        setCarregandoAcesso(false);
        setContextoAcesso(CONTEXTO_ACESSO_INICIAL);
        setErroAcesso('');
        return;
      }

      setCarregandoAcesso(true);
      setErroAcesso('');
      try {
        const userSnap = await getDoc(doc(db, 'users', usuarioLogado.uid));
        const dadosUsuario = userSnap.data() || {};
        const perfisAtivos = Array.isArray(dadosUsuario.perfis_ativos) ? dadosUsuario.perfis_ativos : [];

        if (!ativo) {
          return;
        }

        setContextoAcesso({
          isAdmin: perfisAtivos.includes('admin'),
          isTechnical: perfisAtivos.includes('tecnico'),
          requiresSecurityGate: perfisAtivos.includes('admin') || perfisAtivos.includes('tecnico'),
          perfisAtivos,
          telefone: dadosUsuario.telefone || '',
        });
      } catch (error) {
        console.error('Falha ao carregar o contexto de acesso do painel:', error);

        if (!ativo) {
          return;
        }

        setContextoAcesso(CONTEXTO_ACESSO_INICIAL);
        setErroAcesso(error.message || 'Falha ao validar o acesso ao painel.');
      } finally {
        if (ativo) {
          setCarregandoAcesso(false);
        }
      }
    }

    carregarContextoAcesso();

    return () => {
      ativo = false;
    };
  }, [usuarioLogado?.uid]);

  const caminhoPublico = window.location.pathname.replace(/\/+$/, '') || '/';

  if (caminhoPublico === '/privacidade') {
    return <PaginaPrivacidadePublica />;
  }

  if (caminhoPublico === '/exclusao-de-conta') {
    return <PaginaSolicitacaoExclusaoConta />;
  }

  if (modoRecuperacao) {
    return <RecuperarSenha />;
  }

  if (modoVerificacao) {
    return <VerificarEmail />;
  }

  if (usuarioLogado && carregandoAcesso) {
    return <TelaCarregandoPainel />;
  }

  if (usuarioLogado && erroAcesso) {
    return <TelaFalhaPainel mensagem={erroAcesso} onRetry={() => window.location.reload()} />;
  }

  return (
    <div>
      <SeloHomologacao />
      {usuarioLogado ? (
        contextoAcesso.requiresSecurityGate ? (
          <AdminSecurityGate user={usuarioLogado} contextoAcesso={contextoAcesso}>
            <Dashboard />
          </AdminSecurityGate>
        ) : (
          <Dashboard />
        )
      ) : <Login />}
    </div>
  );
}

export default App;
